import { Text, View, ScrollView, RefreshControl, TouchableOpacity, useWindowDimensions } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { StyleSheet } from 'react-native-unistyles';
import { useEffect, useMemo, useCallback, useState } from 'react';
import Animated, { FadeInDown, Layout } from 'react-native-reanimated';
import TaskCard from '@/components/Tasks/TaskCard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteTask } from '@/data/tasks';
import ContentLoader from '@/components/General/ContentLoader';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { showAlert } from '@/utils/showAlert';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';
import PageTitle from '@/components/General/PageTitle';
import ActionButton from '@/components/General/ActionButton';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import usePlatform from '@/utils/hooks/usePlatform';
import { saveToRecents } from '@/utils/recentlyVisited';
import useSaveShortcut from '@/utils/hooks/useSaveShortcut';
import { isAutomationDueType, isFollowUpDueType } from '@/utils/constants/taskTypes';

// ============ HELPERS ============
const getTaskTypeLabel = (dueType) => {
    switch (dueType) {
        case "INDIVIDUAL": return "Individual Task";
        case "EVENT_VOLUNTEER": return "Volunteer";
        case "TRAINING": return "Training";
        case "PATHS":
        case "AUTOMATIONS": return "Automations";
        case "FOLLOW_UP": return "Follow Up";
        default: return "Service Flow";
    }
};

const getTaskTypeIcon = (dueType) => {
    switch (dueType) {
        case "EVENT_VOLUNTEER": return "people";
        case "EVENT_SERVICE_FLOW": return "list";
        case "PATHS":
        case "AUTOMATIONS": return "git-branch-outline";
        case "FOLLOW_UP": return "heart-outline";
        default: return "clipboard-outline";
    }
};

const getPriorityConfig = (priority) => {
    switch (priority) {
        case "HIGH": return { label: "High", colorVariant: "error", icon: "chevron-triple-up" };
        case "STANDARD": return { label: "Standard", colorVariant: "warning", icon: "chevron-double-up" };
        default: return { label: "Low", colorVariant: "info", icon: "chevron-up" };
    }
};

// ============ INFO ROW ============
const InfoRow = ({ icon, iconLibrary, label, value, children, isEmpty }) => {
    if (!value && !children && !isEmpty) return null;
    return (
        <View style={styles.infoRow}>
            <View style={styles.infoRowLeft}>
                <Unicon name={icon} library={iconLibrary} size={18} colorVariant="textSecondary" />
                <Text style={styles.infoRowLabel}>{label}</Text>
            </View>
            <View style={styles.infoRowRight}>
                {children || <Text style={[styles.infoRowValue, isEmpty && styles.emptyText]}>{value}</Text>}
            </View>
        </View>
    );
};

// ============ TASK INFO PANEL ============
const TaskInfoPanel = ({ taskID }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: task, isFetching } = Query.tasks.byId({ id: taskID });
    const { data: loggedInUser } = Query.people.loggedInUser();

    // Track recently visited
    useEffect(() => {
        if (task?.id) {
            saveToRecents({
                id: task.id,
                url: `/tasks/${task.id}`,
                title: task.title,
                params: { taskID: task.id },
            });
        }
    }, [task?.id, task?.title]);

    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.byId(taskID) });
    const priorityConfig = getPriorityConfig(task?.priority);
    const eventCount = task?.Events?.length || 0;
    const volunteerTeams = task?.VolunteerTeamTasks?.map(vtt => vtt.VolunteerTeam).filter(Boolean) || [];

    return (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh}/>}
                showsVerticalScrollIndicator={false}
            >
                {/* Header Card */}
                <Animated.View entering={FadeInDown.duration(400)} style={styles.headerCard}>
                    {/* Type Badge */}
                    <View style={styles.typeBadge}>
                        <Unicon name={getTaskTypeIcon(task?.dueType)} size={14} colorVariant="tasks" />
                        <Text style={styles.typeBadgeText}>{getTaskTypeLabel(task?.dueType)}</Text>
                    </View>

                    {/* Title Row */}
                    <View style={styles.titleRow}>
                        <View style={styles.iconBadge}>
                            <Unicon name="clipboard-outline" size={24} colorVariant="tasks" />
                        </View>
                        <View style={styles.titleInfo}>
                            <Text style={styles.taskTitle}>{task?.title}</Text>
                            {volunteerTeams.length > 0 && (
                                <Text style={styles.taskSubtitle}>{volunteerTeams.map(t => t.name).join(', ')}</Text>
                            )}
                        </View>
                    </View>

                    {task?.description && (
                        <Text style={styles.taskDescription}>{task.description}</Text>
                    )}

                    {/* Stats Row */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Occurrences</Text>
                            <Text style={styles.statValue}>{task?.TaskInstances?.length || 0}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Events</Text>
                            <Text style={styles.statValue}>{eventCount}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Teams</Text>
                            <Text style={styles.statValue}>{volunteerTeams.length}</Text>
                        </View>
                    </View>

                    {/* Action Buttons — care-task definitions are managed
                        through Follow Up, not the generic task form. */}
                    <View style={styles.actionRow}>
                        {loggedInUser?.can?.("Update Tasks") && !isFollowUpDueType(task?.dueType) && (
                            <ActionButton
                                iconName="create"
                                text="Edit Task"
                                onPress={() => router.push({ pathname: "/tasks/create", params: { id: task?.id } })}
                            />
                        )}
                    </View>
                </Animated.View>

                {/* Details Section */}
                <Animated.View entering={FadeInDown.delay(150).duration(400)} style={styles.infoSection}>
                    <Text style={styles.sectionLabel}>Configuration</Text>

                    <InfoRow icon="layers-outline" label="Task Type" value={getTaskTypeLabel(task?.dueType)} />

                    <InfoRow icon={priorityConfig.icon} iconLibrary="material" label="Priority">
                        <View style={styles.priorityChip(priorityConfig.colorVariant)}>
                            <Unicon name={priorityConfig.icon} library="material" size={14} colorVariant={priorityConfig.colorVariant} />
                            <Text style={styles.priorityChipText(priorityConfig.colorVariant)}>{priorityConfig.label}</Text>
                        </View>
                    </InfoRow>

                    <InfoRow icon="people-outline" label="Teams" value={volunteerTeams.map(t => t.name).join(', ') || "Uncategorized"} isEmpty={volunteerTeams.length === 0} />

                    <InfoRow icon="checkmark-circle-outline" label="Assume Completed">
                        <Text style={[styles.infoRowValue, { color: task?.assumeCompleted ? styles.successColor.color : styles.mutedColor.color }]}>
                            {task?.assumeCompleted ? "Yes" : "No"}
                        </Text>
                    </InfoRow>

                    <InfoRow icon="return-up-forward-outline" label="Allow Forwarding">
                        <Text style={[styles.infoRowValue, { color: task?.allowForwarding ? styles.successColor.color : styles.mutedColor.color }]}>
                            {task?.allowForwarding ? "Yes" : "No"}
                        </Text>
                    </InfoRow>

                    <InfoRow icon="hand-left-outline" label="Self Assignment">
                        <Text style={[styles.infoRowValue, { color: task?.allowSelfAssignment ? styles.successColor.color : styles.mutedColor.color }]}>
                            {task?.allowSelfAssignment ? "Yes" : "No"}
                        </Text>
                    </InfoRow>
                </Animated.View>

                {/* Events Preview */}
                {eventCount > 0 && (
                    <Animated.View entering={FadeInDown.delay(300).duration(400)} style={styles.eventsPreview}>
                        <Text style={styles.sectionLabel}>Linked Events ({eventCount})</Text>
                        {task.Events.slice(0, 5).map((event) => (
                            <TouchableOpacity key={event.id} style={styles.eventItem} onPress={() => router.push(`/events/${event.id}`)}>
                                <View style={styles.eventDot} />
                                <Text style={styles.eventItemText} numberOfLines={1}>{event.name}</Text>
                                <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                            </TouchableOpacity>
                        ))}
                        {eventCount > 5 && (
                            <Text style={styles.moreText}>+{eventCount - 5} more</Text>
                        )}
                    </Animated.View>
                )}
            </ScrollView>
        </View>
    );
};

// ============ OCCURRENCES TAB ============
const OccurrencesTab = ({ taskID }) => {
    const { data: task, isFetching } = Query.tasks.byId({ id: taskID });

    const taskOccurrences = useMemo(() => {
        if (isFetching || !task?.TaskInstances) return [];
        return task.TaskInstances
            .filter(i => !i.cancelledAt && (task.dueType === "INDIVIDUAL" || isAutomationDueType(task.dueType) || isFollowUpDueType(task.dueType) || !!i.EventInstance))
            .sort((a, b) => {
                if (task.dueType === "INDIVIDUAL" || isFollowUpDueType(task.dueType)) {
                    return new Date(a.dueDate) - new Date(b.dueDate);
                }
                if (isAutomationDueType(task.dueType)) {
                    // No date to sort by — keep insertion order (newest first
                    // via createdAt descending).
                    return new Date(b.createdAt) - new Date(a.createdAt);
                }
                return new Date(a.EventInstance?.startTime) - new Date(b.EventInstance?.startTime);
            });
    }, [task?.TaskInstances, task?.dueType, isFetching]);

    if (isFetching) {
        return <View style={styles.centeredContainer}><ActivityIndicator size="large" /></View>;
    }

    return (
        <View style={styles.tabContent}>
            {taskOccurrences.length === 0 ? (
                <EmptyListPlaceholder message="No upcoming occurrences" />
            ) : (
                <ScrollView contentContainerStyle={styles.listPadding} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                    <View style={styles.occurrenceCountRow}>
                        <View style={styles.occurrencesBadge}>
                            <Text style={styles.occurrencesBadgeText}>{taskOccurrences.length} occurrence{taskOccurrences.length !== 1 ? 's' : ''}</Text>
                        </View>
                    </View>
                    <View style={styles.occurrencesList}>
                        {taskOccurrences.map((instance, index) => (
                            <Animated.View
                                key={instance.id}
                                entering={FadeInDown.delay(index * 50).duration(300)}
                                layout={Layout.springify()}
                            >
                                <TaskCard taskInstance={instance} fromScreen={`/tasks/${taskID}`} />
                            </Animated.View>
                        ))}
                    </View>
                </ScrollView>
            )}
        </View>
    );
};

// ============ MAIN SCREEN ============
const TaskScreen = () => {
    const { taskID } = useLocalSearchParams();
    const [tabIndex, setTabIndex] = useState(0);

    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { width } = useWindowDimensions();
    const { isNative } = usePlatform();
    const isWideScreen = width >= 900;

    if (!taskID) {
        router.canGoBack() ? router.back() : router.replace("/");
    }

    const { data: task, isFetching } = Query.tasks.byId({ id: taskID });
    const { data: loggedInUser } = Query.people.loggedInUser();
    const saveShortcut = useSaveShortcut(loggedInUser?.id);

    const deleteTaskMutation = useMutation({
        mutationFn: deleteTask,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
            router.canGoBack() ? router.back() : router.replace("/");
        }
    });

    useEffect(() => {
        const options = [];
        if (task?.id) {
            options.push({
                name: "Save to Shortcuts",
                icon: dropdownIcons.favorite,
                onSelect: () => saveShortcut({ screen: `/tasks/${task.id}`, label: task?.title || 'Task', icon: 'checkbox-outline' })
            });
        }
        if (loggedInUser?.can?.("Update Tasks") && !isFollowUpDueType(task?.dueType)) {
            options.push({
                name: "Edit Task",
                icon: dropdownIcons.edit,
                onSelect: () => router.push({ pathname: "/tasks/create", params: { id: task?.id } })
            });
        }
        if (loggedInUser?.can?.("Delete Tasks") && !isFollowUpDueType(task?.dueType)) {
            options.push({
                name: "Delete Task",
                destructive: true,
                icon: dropdownIcons.delete,
                onSelect: () => {
                    showAlert({
                        title: "Delete Task",
                        subtitle: "Are you sure you want to delete this task? This will also delete all occurrences. This cannot be undone.",
                        isDestructive: true,
                        onConfirm: () => deleteTaskMutation.mutate({ taskID }),
                        confirmText: "Delete"
                    });
                }
            });
        }

        navigation.setOptions({
            headerTitle: () => <PageTitle title={task?.title} subtitle="Parent Task" />,
            headerRight: options.length === 0 ? null : () => <DropdownMenu testID="HeaderMenu" options={options} />,
        });
    }, [navigation, task, loggedInUser, saveShortcut]);

    // ============ TAB ROUTES ============
    const mobileRoutes = useMemo(() => [
        { key: 'info', title: 'Info' },
        { key: 'occurrences', title: 'Occurrences' },
    ], []);

    const activityRoutes = useMemo(() => [
        { key: 'occurrences', title: 'Occurrences' },
    ], []);

    const renderTabBar = useCallback((props) => <ReachTabBar {...props} />, []);

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'info':
                return <TaskInfoPanel taskID={taskID} />;
            case 'occurrences':
                return <OccurrencesTab taskID={taskID} />;
            default:
                return null;
        }
    }, [taskID]);

    // ============ LOADING STATE ============
    if (!task?.id && isFetching) {
        return (
            <Animated.View entering={FadeInDown.duration(300)} style={styles.loadingContainer}>
                <View style={styles.loadingHeaderRow}>
                    <ContentLoader containerHeight={52} containerWidth={52} borderRadius={10} spacingVertical={0} />
                    <View style={styles.loadingHeaderInfo}>
                        <ContentLoader containerHeight={22} containerWidth="70%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                        <ContentLoader containerHeight={14} containerWidth="40%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                    </View>
                </View>
                <View style={styles.loadingStatsRow}>
                    <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                    <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                    <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                </View>
                <ContentLoader containerHeight={180} borderRadius={12} staggerPosition={4} />
            </Animated.View>
        );
    }

    if (!task?.id && !isFetching) {
        return <EmptyListPlaceholder type="404" message="Task not found" />;
    }

    // ============ WIDE SCREEN LAYOUT ============
    if (isWideScreen) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    <TaskInfoPanel taskID={taskID} />
                    <View style={styles.rightPanel}>
                        <TabView
                            navigationState={{ index: tabIndex, routes: activityRoutes }}
                            renderScene={renderScene}
                            onIndexChange={setTabIndex}
                            renderTabBar={renderTabBar}
                            swipeEnabled={isNative}
                            lazy
                        />
                    </View>
                </View>
            </View>
        );
    }

    // ============ MOBILE LAYOUT ============
    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: tabIndex, routes: mobileRoutes }}
                renderScene={renderScene}
                onIndexChange={setTabIndex}
                renderTabBar={renderTabBar}
                swipeEnabled={isNative}
                lazy
            />
        </View>
    );
};

const styles = StyleSheet.create((theme, rt) => ({
    container: { flex: 1, backgroundColor: 'transparent' },

    // Loading State
    loadingContainer: { flex: 1, padding: 20, maxWidth: 500, width: rt.isLandscape ? '50%' : '100%' },
    loadingHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 20 },
    loadingHeaderInfo: { flex: 1, marginLeft: 16, justifyContent: 'center' },
    loadingStatsRow: { flexDirection: 'row', gap: 10, marginBottom: 20 },
    loadingStatItem: { flex: 1 },

    // Two Column Layout
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420 },
    leftPanelContent: { padding: 16, paddingBottom: 100 },
    rightPanel: { flex: 2 },

    // Tab Content
    tabContent: { flex: 1 },
    listPadding: { padding: 16, paddingBottom: 100 },
    centeredContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },

    // Header Card
    headerCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 16,
        padding: 20,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 3,
    },

    // Type Badge
    typeBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.tasksContainer,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 6,
        marginBottom: 16,
        alignSelf: 'flex-start',
        gap: 6,
    },
    typeBadgeText: { color: theme.colors.tasks, fontSize: 12, fontFamily: theme.fonts.bold },

    // Title Row
    titleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
    iconBadge: {
        width: 52,
        height: 52,
        backgroundColor: theme.colors.tasksContainer,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
    },
    titleInfo: { flex: 1, marginLeft: 14 },
    taskTitle: { fontSize: 18, fontFamily: theme.fonts.heading, color: theme.colors.text },
    taskSubtitle: { fontSize: 13, color: theme.colors.textSecondary, marginTop: 2 },
    taskDescription: {
        fontSize: 14,
        color: theme.colors.textSecondary,
        lineHeight: 20,
        marginBottom: 16,
    },

    // Stats Row
    statsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: theme.colors.border,
        marginBottom: 16,
    },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Action Buttons
    actionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },

    // Info Section
    infoSection: {
        backgroundColor: theme.colors.surface,
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 4,
        elevation: 2,
    },
    sectionLabel: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        marginBottom: 12,
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    infoRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, maxWidth: 160 },
    infoRowLabel: { fontSize: 13, color: theme.colors.textSecondary },
    infoRowRight: { flex: 2, alignItems: 'flex-end' },
    infoRowValue: { fontSize: 14, color: theme.colors.text, textAlign: 'right' },
    emptyText: { fontSize: 14, color: theme.colors.textSecondary, fontStyle: 'italic', textAlign: 'right' },

    // Priority Chip
    priorityChip: (colorVariant) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: theme.colors[colorVariant + "Container"] || theme.colors.surfaceVariant,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
    }),
    priorityChipText: (colorVariant) => ({
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors[colorVariant] || theme.colors.textSecondary,
    }),

    // Color helpers
    successColor: { color: theme.colors.success },
    infoColor: { color: theme.colors.info },
    mutedColor: { color: theme.colors.textSecondary },

    // Events Preview
    eventsPreview: {
        backgroundColor: theme.colors.surface,
        borderRadius: 16,
        padding: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 4,
        elevation: 2,
    },
    eventItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    eventDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: theme.colors.tasks,
    },
    eventItemText: { flex: 1, fontSize: 14, color: theme.colors.text },
    moreText: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 8, fontStyle: 'italic' },

    // Occurrences
    occurrenceCountRow: { marginBottom: 12 },
    occurrencesBadge: {
        backgroundColor: theme.colors.primaryContainer,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        alignSelf: 'flex-start',
    },
    occurrencesBadgeText: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
    },
    occurrencesList: { gap: 8 },
}));

export default TaskScreen;